(ns dashboard.entity.document.paragraphs
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [clojure.string :as str]
            [dashboard.entity.analysis.modals :as analysis.modals]
            [dashboard.entity.analysis.tree :as analysis.tree]
            [dashboard.entity.document.evidence :as evidence]
            [dashboard.entity.document.js-dom :as js.dom]))

(defn document-paragraphs-recommended-analysis
  "List of the top n=3 analyses for a given paragraph.
   Used to display the recommended analysis for a selected paragraph."
  [{:keys [active-highlight
           is-obsolete
           on-create-paragraph-evidence
           paragraphs]}]
  (let [{:keys [id paragraph-text page]
         :as paragraph} (->> paragraphs
                             (filter
                              (fn [{:keys [id]}]
                                (= (str id) (str (:document-paragraph-id active-highlight)))))
                             first)]
    [:<>
     [:div [:b "Top Recommended Analysis"]]
     [evidence/abridged-content-blockquote paragraph-text]
     (->> paragraph
          :analysis
          (mapv
           (fn [{:keys [construct_id construct linked]}]
             [:p
              construct
              [:button.button.is-small.is-primary.ml-3
               {:disabled (or linked is-obsolete)
                :on-click #(on-create-paragraph-evidence
                            page
                            id
                            paragraph-text
                            (->> (str "paragraph-" id "-" (dec page))
                                 (.getElementById js/document))
                            #{construct_id})}
               (if linked
                 [:i.icon.fas.fa-circle-check]
                 [:i.icon.fas.fa-circle-plus])]]))
          (into [:<>]))]))

(defn paragraph-data
  "Line-item in the list of analysis and the paragraphs that are
   returned as most similar to the given asa (analysis)."
  [{:keys [is-obsolete
           on-create-paragraph-evidence
           on-create-selection-evidence
           paragraphs-by-top-analysis
           selected-construct
           selected-text
           set-active-highlight]}
   props
   asa]
  [:div
   (let [count (count (get paragraphs-by-top-analysis (:construct-id asa)))]
     [:<>
      [analysis.tree/ListItemAnalysisRow
       props
       (assoc asa
              :construct
              [:<>
               (str (:construct asa)
                    (when-not (zero? count)
                      (str " ("
                           count
                           " paragraphs)")))
               [:button.button.is-secondary.is-small.is-circular.ml-3.is-rounded
                {:on-click #(on-create-selection-evidence asa)
                 :disabled (or (str/blank? selected-text)
                               is-obsolete)}
                [:i.icon.fas.fa-circle-plus]]])]])

   (when (= selected-construct (:construct-id asa))
     [:div.document-viewer--related-content-for-asa
      [:h6.title.is-6.mt-4.mb-1
       "Related Content for "
       (:construct asa)]
      (->> (get paragraphs-by-top-analysis selected-construct)
           (sort-by :page)
           (mapv
            (fn [{:keys [id page paragraph-text analysis] :as p}]
              (let [elem (->> (str "paragraph-" id "-" (dec page))
                              (.getElementById js/document))
                    disabled (->> analysis
                                  (filterv (comp (partial = (:construct-id asa))
                                                 :construct_id))
                                  first
                                  :linked
                                  boolean)]
                [:blockquote
                 "Page "
                 page
                 ") "
                 [:a
                  {:disabled is-obsolete
                   :on-click
                   (fn []
                     (when-not (nil? elem)
                       (js.dom/remove-class-from-all "highlight-info--active-highlight")

                       (set-active-highlight
                        {:document-paragraph-id id
                         :type :paragraph})

                       (-> elem
                           .-className
                           (set! (str elem.className " "
                                      "highlight-info--active-highlight")))
                       (.scrollIntoView
                        elem
                        #js{:behavior "smooth"
                            :block "center"})))}
                  [:span paragraph-text]]
                 [:button.button.is-small.is-primary.ml-3
                  {:disabled (or disabled is-obsolete)
                   :on-click #(on-create-paragraph-evidence
                               page
                               id
                               paragraph-text
                               elem
                               #{selected-construct})}

                  (if disabled
                    [:i.icon.fas.fa-circle-check]
                    [:i.icon.fas.fa-circle-plus])]])))
           (into [:div.content.my-3]))])])

(defn paragraph-recommendations
  "Left-hand menu for paragraph recommendations by analysis."
  [{:keys [all-analysis
           is-obsolete
           loading-paragraphs
           on-create-paragraph-evidence
           on-create-selection-evidence
           paragraphs
           selected-construct
           selected-text
           set-active-highlight
           set-selected-construct
           set-threshold
           threshold]}]
  (let [[ref set-ref] (react/useState nil)]
    (react/useEffect
     (fn [] (when (and (not loading-paragraphs)
                       ref)
              (.focus ref)))
     (array loading-paragraphs))

    [:div.document-viewer--highlight-info
     [:h5.title.is-5 "Recommended Analysis by Paragraph"]
     [:div.level-left.mb-2
      [:div.level-left.mr-3 "Minimum Similarity Threshold"]
      [:input.input {:type :number
                     :ref set-ref
                     :value threshold
                     :on-change (fn [e] (set-threshold e.target.value))
                     :step 5
                     :disabled loading-paragraphs
                     :min 10
                     :max 90}]]
     (let [paragraphs-by-top-analysis (->> paragraphs
                                           (group-by (comp :construct_id
                                                           first
                                                           :analysis)))]
       [:f> analysis.tree/AnalysisTree
        {:hide-category false
         :hide-category-selection false
         :on-click-construct set-selected-construct
         :on-select set-selected-construct
         :preselected-version-ids #{}
         :selected-version-ids #{}
         :on-click-state (fn [construct-id structure]
                           (analysis.modals/show-editor-modal
                            :edit
                            construct-id
                            (-> structure :applied-analyses first)))
         :category (or "Policy"
                       "Operations (process)")
         :loading false
         :analyses (vec all-analysis)
         :search-result-construct-ids (->> paragraphs
                                           (mapv (comp first :analysis))
                                           flatten
                                           distinct
                                           (mapv :construct_id)
                                           set)
         :active-query true ;(not (str/blank? query))
         :row-component (partial paragraph-data
                                 {:selected-construct selected-construct
                                  :selected-text selected-text
                                  :set-active-highlight set-active-highlight
                                  :is-obsolete is-obsolete
                                  :on-create-paragraph-evidence on-create-paragraph-evidence
                                  :on-create-selection-evidence on-create-selection-evidence
                                  :paragraphs-by-top-analysis paragraphs-by-top-analysis})}])]))